import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Search, X } from 'lucide-react';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import FilterMenu from '@/components/ui/filter-menu';
import { Input } from '@/components/ui/input';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import { debounce } from '@/lib/utils';
import { SORT_OPTIONS } from '@/redux/post/post.utils';

const WRITE_DELAY_MS = 400;

/** Search + sort for the profile feed, sitting in the navbar chrome like the feed's. */
// fallow-ignore-next-line complexity
function ProfileSearchRow() {
  const { getSearchParam, appendSearchParam, removeSearchParam } =
    useSearchParamActions();
  const view = getSearchParam('view');
  const urlSearch = getSearchParam('search') ?? '';
  const sort = getSearchParam('sort') ?? SORT_OPTIONS[0].value;

  const [text, setText] = useState(urlSearch);

  // The hook's writers are rebuilt on every URL change, so a debounced closure
  // that captured them once would clobber sibling params with a stale snapshot.
  const writers = useRef({ appendSearchParam, removeSearchParam });
  useEffect(() => {
    writers.current = { appendSearchParam, removeSearchParam };
  });

  // What this row itself last wrote, so the sync-from-URL effect can ignore its
  // own write echoing back and not revert a mid-debounce keystroke.
  const lastWritten = useRef<string | null>(null);

  const write = useMemo(
    () =>
      debounce((value: string) => {
        const trimmed = value.trim();
        lastWritten.current = trimmed;
        if (trimmed) writers.current.appendSearchParam('search', trimmed);
        else writers.current.removeSearchParam('search');
      }, WRITE_DELAY_MS),
    []
  );

  useEffect(() => {
    if (urlSearch === lastWritten.current) return;
    setText(urlSearch);
  }, [urlSearch]);

  useEffect(() => () => write.cancel(), [write]);

  const clear = () => {
    write.cancel();
    lastWritten.current = '';
    setText('');
    removeSearchParam('search');
  };

  const activeSort =
    SORT_OPTIONS.find((option) => option.value === sort) ?? SORT_OPTIONS[0];

  // Bookmarks and Download Logs are their own lists; neither reads these params.
  if (view === 'bookmarks' || view === 'downloadLogs') return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative w-full min-w-0 md:w-auto md:flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-black-400" />
        <Input
          type="text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            write(e.target.value);
          }}
          placeholder="Search by title or group name..."
          aria-label="Search projects and groups"
          className="w-full rounded-full pl-9 pr-9"
        />
        {text && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center"
          >
            <X className="h-4 w-4 text-black-400 hover:text-black-600" />
          </button>
        )}
      </div>

      <FilterMenu
        label={
          <>
            <ArrowUpDown className="h-4 w-4" />
            {activeSort.label}
          </>
        }
      >
        {SORT_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => appendSearchParam('sort', option.value)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </FilterMenu>
    </div>
  );
}

export default ProfileSearchRow;
